/* =============================================
   App / Vendor Scripts — Art of Tea

   Scripts that patch third-party app widgets we don't control (Recharge, …).
   Loaded deferred by layout/theme.liquid via the base theme's generic
   app-overrides hook, so no app names appear in shared theme code. Style-side
   counterpart is _public/app-overrides.css.

   Keep theme-owned behaviour out of here; that belongs in `_src/`.
   ============================================= */

/* =============================================
   Recharge Subscription Widget — Shadow DOM Patches

   Injects CSS into the widget's shadow roots (and its nested rc-radio-icon /
   rc-benefits elements), which host-level CSS cannot reach, plus the small DOM
   patches that go with them. Re-runs on Lit re-renders and on widgets inserted
   after load.
   ============================================= */

(function () {
  'use strict';

  var WIDGET_STYLE_ID   = 'rc-custom-shadow-styles';
  var RADIO_STYLE_ID    = 'rc-radio-icon-custom';
  var BENEFITS_STYLE_ID = 'rc-benefits-custom';

  var SVG_UNSELECTED = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'%3E%3Crect width='18' height='18' rx='9' fill='%239E9E9E' fill-opacity='0.3'/%3E%3C/svg%3E\")";
  var SVG_SELECTED   = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'%3E%3Crect width='18' height='18' rx='9' fill='%239E9E9E' fill-opacity='0.3'/%3E%3Ccircle cx='9' cy='9' r='5' fill='%23D6954B'/%3E%3C/svg%3E\")";

  /* Custom 11×8 checkmark (gold stroke) for rc-benefits items */
  var SVG_CHECK = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='8' viewBox='0 0 11 8' fill='none'%3E%3Cpath d='M9.85862 1.13135L3.85862 7.13135L1.13135 4.40408' stroke='%23D6954B' stroke-width='1.6' stroke-linecap='square' stroke-linejoin='round'/%3E%3C/svg%3E\")";

  /* ──────────────────────────────────────────────────────
     CSS injected into the widget shadow root.
     !important is required throughout — Recharge's Lit
     adoptedStyleSheets may otherwise win on specificity.
  ────────────────────────────────────────────────────── */
  var SHADOW_CSS = [

    /* Fieldset reset */
    '.rc-purchase-option-group { border: none !important; padding: 0 !important; margin: 0 !important; }',

    /* Stack the two cards */
    '.rc-selection__root { display: flex !important; flex-direction: column !important; gap: 12px !important; }',

    /* ── Card ── */
    '.rc-purchase-option { border: 1.5px solid #9E9E9E !important; border-radius: 0 !important; padding: 18px !important; background: #fff !important; box-sizing: border-box !important; transition: border-color 0.15s ease !important; }',
    '@media (max-width: 768px) { .rc-purchase-option { padding: 12px !important; } }',
    '.rc-purchase-option[rc-selected] { border-color: #231F20 !important; }',
    '.rc-purchase-option:not(:last-child) { margin-bottom: 0 !important; }',

    /* ── Badge ──
       font-size:0 collapses Recharge's original text ("Save up to 15%").
       ::before injects our replacement text. This survives Lit re-renders
       because it's pure CSS — no DOM text-node change needed. */
    '.rc-purchase-option__badge { position: static !important; top: auto !important; right: auto !important; left: auto !important; bottom: auto !important; transform: none !important; display: inline-flex !important; align-items: center !important; background-color: #0F8354 !important; font-size: 0 !important; padding: 6px !important; border-radius: 0 !important; white-space: nowrap !important; flex-shrink: 0 !important; }',
    '.rc-purchase-option__badge::before { content: "SAVE 15%" !important; font-size: 12px !important; line-height: 0.8 !important; font-weight: 700 !important; color: #fff !important; letter-spacing: 0.04em !important; text-transform: uppercase !important; }',
    /* ── Label row: selector (left) + prices (right) ── */
    '.rc-purchase-option__label { display: flex !important; flex-direction: row !important; align-items: center !important; justify-content: space-between !important; cursor: pointer !important; gap: 8px !important; width: 100% !important; padding: 0 !important; }',

    /* ── Selector: radio icon + option text + badge ── */
    '.rc-purchase-option__selector { display: flex !important; flex-direction: row !important; align-items: center !important; gap: 8px !important; flex: 1 !important; min-width: 0 !important; font-size: 16px !important; font-weight: 500 !important; color: #231F20 !important; line-height: normal !important; }',
    '@media (max-width: 768px) { .rc-purchase-option__selector { font-size: 14px !important; } }',

    /* Keep native radio accessible but invisible */
    '.rc-purchase-option__input { position: absolute !important; opacity: 0 !important; width: 0 !important; height: 0 !important; pointer-events: none !important; }',

    /* Radio icon element */
    'rc-radio-icon { display: block !important; width: 18px !important; height: 18px !important; min-width: 18px !important; flex-shrink: 0 !important; }',

    /* ── Prices ── */
    '.rc-purchase-option__prices { display: flex !important; flex-direction: row !important; align-items: center !important; gap: 6px !important; flex-shrink: 0 !important; margin: 0 !important; }',
    '.rc-price, [part~="rc-purchase-option__discounted-price"].rc-price { font-size: 18px !important; font-weight: 700 !important; color: #231F20 !important; line-height: 1.35 !important; }',
    '@media (max-width: 768px) { .rc-price, [part~="rc-purchase-option__discounted-price"].rc-price { font-size: 14px !important; } }',
    '.rc-price.strike-through, .rc-price.text-muted { font-size: 16px !important; font-weight: 400 !important; color: #9E9E9E !important; text-decoration: line-through !important; line-height: 1.35 !important; }',
    '@media (max-width: 768px) { .rc-price.strike-through, .rc-price.text-muted { font-size: 12px !important; } }',
    '[part~="rc-purchase-option__discounted-price"] .rc-price, [part~="rc-purchase-option__discounted-price"].rc-price { color: #0F8354 !important; font-weight: 700 !important; text-decoration: none !important; }',
    /* Swap visual order: discounted (green) before strikethrough */
    '[part~="rc-purchase-option__discounted-price"] { order: -1 !important; }',

    /* ── Sub-container: benefits + learn-more ── */
    '.rc-purchase-option__sub-container { margin-top: 16px !important; padding: 0 !important; display: flex !important; flex-direction: column !important; gap: 8px !important; }',
    '.rc-purchase-option__checked-indicator { margin-right: 0 !important; }',

    /* ── Delivery frequency: hidden to match design ── */
    'rc-selling-plans { display: none !important; }',

    /* ── Learn-more trigger ── */
    'rc-learn-more::part(rc-learn-more__trigger), rc-learn-more::part(rc-learn-more__trigger-compact) { display: inline !important; background: none !important; border: none !important; padding: 0 !important; cursor: pointer !important; font-family: inherit !important; font-size: 14px !important; line-height: 1.6 !important; color: #4a4a4a !important; text-decoration: underline !important; text-underline-offset: 2px !important; }',

    /* ── Benefits fallback: applies if rc-benefits has NO shadow root ──
       Recharge may render rc-benefits as a plain custom element without
       its own shadow root; these selectors handle that case. */
    'rc-benefits ul { list-style: none !important; padding: 0 !important; margin: 0 !important; display: flex !important; flex-direction: column !important; gap: 4px !important; }',
    'rc-benefits li { display: flex !important; align-items: center !important; gap: 8px !important; font-size: 12px !important; font-weight: 500 !important; color: #231F20 !important; text-transform: uppercase !important; letter-spacing: 0.06em !important; line-height: 1.4 !important; }',
    'rc-benefits svg { display: none !important; }',
    'rc-benefits li::before { content: "" !important; display: block !important; width: 11px !important; height: 8px !important; min-width: 11px !important; flex-shrink: 0 !important; mask-image: none !important; -webkit-mask-image: none !important; background-color: transparent !important; background-image: ' + SVG_CHECK + ' !important; background-repeat: no-repeat !important; background-size: contain !important; background-position: center !important; }',

    /* Accessibility helper */
    '.visually-hidden { position: absolute !important; width: 1px !important; height: 1px !important; padding: 0 !important; margin: -1px !important; overflow: hidden !important; clip: rect(0,0,0,0) !important; white-space: nowrap !important; border: 0 !important; }'

  ].join('\n');

  /* ── rc-radio-icon shadow root CSS ── */
  var RADIO_CSS = [
    ':host { display: block !important; width: 18px !important; height: 18px !important; min-width: 18px !important; background-repeat: no-repeat !important; background-position: center !important; background-size: contain !important; background-image: ' + SVG_UNSELECTED + ' !important; }',
    ':host > * { display: none !important; }',
    ':host([checked]) { background-image: ' + SVG_SELECTED + ' !important; }'
  ].join('\n');

  /* ── rc-benefits shadow root CSS ── */
  var BENEFITS_CSS = [
    ':host { display: block !important; }',
    'ul { list-style: none !important; padding: 0 !important; margin: 0 !important; display: flex !important; flex-direction: column !important; gap: 8px !important; }',
    'li { display: flex !important; align-items: center !important; gap: 8px !important; font-size: 13px !important; font-weight: 500 !important; color: #231F20 !important; text-transform: uppercase !important; letter-spacing: 0.06em !important; line-height: 1.4 !important; }',
    '.rc-benefits__list { color: #4a4a4a !important; font-size: 13px !important; letter-spacing: 2px !important; line-height: 1.8 !important; }',
    '.rc-benefits__list ul { gap: 10px !important; }',
    '.rc-benefits__list li { padding-left: 1rem !important; margin-bottom: 0 !important; font-size: 13px!important; line-height: 1.8!important; letter-spacing: 2px!important; }',
    '.rc-benefits__list li::before { top: unset !important; }',
    '@media (max-width: 768px) { .rc-benefits__list li { font-size: 10px !important; } }',
    /* Hide Recharge's circle-check icon */
    'svg { display: none !important; }',
    /* Inject our custom checkmark as a pseudo-element.
       Recharge uses mask-image + background-color on li::before to render their icon.
       We clear both so our background-image SVG is visible instead. */
    'li::before { content: "" !important; display: block !important; width: 11px !important; height: 8px !important; min-width: 11px !important; flex-shrink: 0 !important; mask-image: none !important; -webkit-mask-image: none !important; background-color: transparent !important; background-image: ' + SVG_CHECK + ' !important; background-repeat: no-repeat !important; background-size: contain !important; background-position: center !important; }'
  ].join('\n');

  /* ── Helpers ── */

  function addStyle(root, id, css) {
    if (root.querySelector('#' + id)) return;
    var s = document.createElement('style');
    s.id = id;
    s.textContent = css;
    root.appendChild(s);
  }

  /* Replace the first matching text node inside `parent`. */
  function replaceText(parent, pattern, replacement) {
    if (!parent) return;
    Array.from(parent.childNodes).some(function (node) {
      if (node.nodeType === 3 && pattern.test(node.textContent)) {
        node.textContent = node.textContent.replace(pattern, replacement);
        return true;
      }
    });
  }

  /* Track which widgets already have a shadow observer so we don't double-attach. */
  var observedWidgets = new WeakSet();

  function injectIntoWidget(widget) {
    var root = widget.shadowRoot;
    if (!root) return;

    /* 1 — Main shadow CSS */
    addStyle(root, WIDGET_STYLE_ID, SHADOW_CSS);

    /* 2 — Move badge inline (from before <label> into .rc-purchase-option__selector)
           so it sits after the option text instead of floating to the card corner. */
    var badge    = root.querySelector('.rc-purchase-option__badge');
    var selector = root.querySelector('.rc-purchase-option__selector');
    if (badge && selector && badge.parentNode !== selector) {
      selector.appendChild(badge);
    }

    /* 3 — Strip "save" from "Subscribe & save" → "Subscribe &"
           The badge already visually shows "SAVE 15%", so the word is redundant. */
    replaceText(selector, /subscribe\s*&\s*save/i, 'Subscribe &');

    /* 3b — Rename "One-time" → "One-time purchase" */
    var onetimeSelector = root.querySelector('[part~="rc-purchase-option__selector_onetime"]');
    replaceText(onetimeSelector, /^One-time$/i, 'One-time purchase');

    /* 4 — Change "Learn more" link text inside rc-learn-more shadow root */
    root.querySelectorAll('rc-learn-more').forEach(function (learnMore) {
      var lr = learnMore.shadowRoot;
      if (!lr) return;
      /* Try common trigger selectors */
      var trigger = lr.querySelector('[part~="rc-learn-more__trigger"], [part~="rc-learn-more__trigger-compact"], button, a');
      if (!trigger) return;
      /* Replace any non-empty text node — avoids touching child elements (icons etc.) */
      Array.from(trigger.childNodes).forEach(function (node) {
        if (node.nodeType === 3 && node.textContent.trim().length > 0) {
          node.textContent = node.textContent.replace(/\S.*/, 'How Subscriptions Works?');
        }
      });
    });

    /* 5 & 6 — Nested custom element shadow roots.
       rc-radio-icon and rc-benefits are Lit components that render
       asynchronously inside the widget shadow root. Attempt injection
       immediately, then retry at 100 / 300 / 600 ms to catch late renders. */
    injectNested(root);
    [100, 300, 600].forEach(function (delay) {
      setTimeout(function () { injectNested(root); }, delay);
    });

    /* 7 — Watch the widget's own shadow root for Lit re-renders triggered by
       option selection changes. The outer document.body observer never fires
       for mutations inside a shadow root, so styles and DOM patches would
       otherwise be lost whenever Lit updates the component tree. */
    if (!observedWidgets.has(widget)) {
      observedWidgets.add(widget);
      var shadowPending = false;
      new MutationObserver(function () {
        if (shadowPending) return;
        shadowPending = true;
        requestAnimationFrame(function () {
          shadowPending = false;
          injectIntoWidget(widget);
        });
      }).observe(root, { childList: true, subtree: true });
    }
  }

  function injectNested(root) {
    root.querySelectorAll('rc-radio-icon').forEach(function (icon) {
      if (icon.shadowRoot) addStyle(icon.shadowRoot, RADIO_STYLE_ID, RADIO_CSS);
    });
    root.querySelectorAll('rc-benefits').forEach(function (benefits) {
      if (benefits.shadowRoot) addStyle(benefits.shadowRoot, BENEFITS_STYLE_ID, BENEFITS_CSS);
    });
  }

  function scan() {
    document.querySelectorAll('recharge-subscription-widget').forEach(function (widget) {
      var root = widget.shadowRoot;
      if (root && root.children.length > 0) {
        injectIntoWidget(widget);
      } else {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { injectIntoWidget(widget); });
        });
      }
    });
  }

  /* Initial run */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  /* Belt-and-suspenders: also fire after full page load */
  window.addEventListener('load', scan);

  /* Watch for widgets inserted dynamically (quick-view, variant changes, etc.) */
  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () { pending = false; scan(); });
  }).observe(document.body, { childList: true, subtree: true });

})();

/* =============================================
   Junip Review Summary — Review Count Label

   Junip hardcodes the summary count as "(27)" in its widget bundle:
     `<div class="junip-product-summary-review-count">(${count})</div>`
   There is no display setting for it, so the only way to read "27 Reviews" is to
   rewrite the node after Junip paints it.

   Junip repaints on variant changes and on cards inserted by filter/sort/paging,
   so this observes rather than running once. The original digits are kept in a
   data attribute so a repaint can't compound the label.
   ============================================= */

(function () {
  'use strict';

  var COUNT_SELECTOR = '.junip-product-summary-review-count';
  var SINGULAR = 'Review';
  var PLURAL   = 'Reviews';

  function relabel(node) {
    var raw = node.dataset.junipRawCount;

    if (raw === undefined) {
      /* First pass: capture the digits Junip rendered, e.g. "(27)" -> "27" */
      var digits = (node.textContent || '').replace(/\D+/g, '');
      if (!digits) return;
      raw = digits;
      node.dataset.junipRawCount = raw;
    }

    var label = raw === '1' ? SINGULAR : PLURAL;
    var next  = raw + ' ' + label;
    if (node.textContent.trim() !== next) node.textContent = next;
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll(COUNT_SELECTOR);
    for (var i = 0; i < nodes.length; i++) relabel(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () { pending = false; scan(); });
  }).observe(document.body, { childList: true, subtree: true });

})();
